/**
 * UIState handles transient UI state tracking (seen/unseen items and waves).
 *
 * Provides a focused API for UI notification state while keeping
 * actual state in GameState.state for save compatibility.
 *
 * Pattern: Same as WaveStateManager - wraps state access, doesn't own state.
 */
export class UIState {
    /**
     * @param {GameState} gameState - Reference to main game state
     */
    constructor(gameState) {
        this.gameState = gameState;
    }

    // ===== State Accessors (shorthand) =====

    /**
     * Get inventory state object
     */
    get inventory() {
        return this.gameState.state.inventory;
    }

    /**
     * Get waves state object
     */
    get waves() {
        return this.gameState.state.waves;
    }

    // ===== Item Seen State =====

    /**
     * Check if a specific item has been seen by the user
     * @param {string} itemId - Item ID to check
     * @returns {boolean} True if seen
     */
    hasSeenItem(itemId) {
        return this.inventory.seenItemIds?.includes(itemId) || false;
    }

    /**
     * Mark a specific item as seen
     * @param {string} itemId - Item ID to mark as seen
     */
    markItemSeen(itemId) {
        if (!this.inventory.seenItemIds) {
            this.inventory.seenItemIds = [];
        }
        if (!this.inventory.seenItemIds.includes(itemId)) {
            this.inventory.seenItemIds.push(itemId);
            this.gameState.save();
        }
    }

    /**
     * Get count of unseen items in inventory
     * @returns {number} Number of unseen items
     */
    getUnseenItemCount() {
        const seen = this.inventory.seenItemIds || [];
        return this.inventory.items.filter(item => !seen.includes(item.id)).length;
    }

    /**
     * Mark all current items as seen
     */
    markAllItemsSeen() {
        const itemIds = this.inventory.items.map(item => item.id);
        this.inventory.seenItemIds = [...new Set([
            ...(this.inventory.seenItemIds || []),
            ...itemIds
        ])];
        this.gameState.save();
    }

    /**
     * Clean up seen item IDs that are no longer in inventory
     */
    cleanupSeenItems() {
        const currentIds = this.inventory.items.map(item => item.id);
        this.inventory.seenItemIds = (this.inventory.seenItemIds || [])
            .filter(id => currentIds.includes(id));
    }

    // ===== Wave Seen State =====

    /**
     * Check if a wave milestone has been seen by the user
     * @param {number} milestone - The wave milestone
     * @returns {boolean} True if seen
     */
    hasSeenWave(milestone) {
        return this.waves.seenWaveMilestones?.includes(milestone) || false;
    }

    /**
     * Mark a wave milestone as seen
     * @param {number} milestone - The wave milestone to mark as seen
     */
    markWaveSeen(milestone) {
        if (!this.waves.seenWaveMilestones) {
            this.waves.seenWaveMilestones = [];
        }
        if (!this.waves.seenWaveMilestones.includes(milestone)) {
            this.waves.seenWaveMilestones.push(milestone);
            this.gameState.save();
        }
    }

    /**
     * Get count of unseen waves
     * @returns {number} Number of unseen available waves
     */
    getUnseenWaveCount() {
        const seen = this.waves.seenWaveMilestones || [];
        return (this.waves.availablePacks || []).filter(p => p && !seen.includes(p.milestone)).length;
    }

    /**
     * Mark all current waves as seen
     */
    markAllWavesSeen() {
        const milestones = (this.waves.availablePacks || []).filter(p => p).map(p => p.milestone);
        this.waves.seenWaveMilestones = [...new Set([
            ...(this.waves.seenWaveMilestones || []),
            ...milestones
        ])];
        this.gameState.save();
    }

    /**
     * Clean up seen milestones that no longer have corresponding packs
     */
    cleanupSeenWaves() {
        const activeMilestones = (this.waves.availablePacks || []).map(p => p.milestone);
        this.waves.seenWaveMilestones = (this.waves.seenWaveMilestones || [])
            .filter(m => activeMilestones.includes(m));
    }

    // ===== Collection Seen State =====

    /**
     * Get collection state object
     */
    get collection() {
        return this.gameState.state.collection;
    }

    /**
     * Check if a collection discovery has been seen by the user
     * @param {string} baseId - Item base ID to check
     * @returns {boolean} True if seen
     */
    hasSeenCollectionItem(baseId) {
        return this.collection?.seenDiscoveries?.includes(baseId) || false;
    }

    /**
     * Mark a collection discovery as seen
     * @param {string} baseId - Item base ID to mark as seen
     */
    markCollectionItemSeen(baseId) {
        if (!this.collection) return;
        if (!this.collection.seenDiscoveries) {
            this.collection.seenDiscoveries = [];
        }
        if (!this.collection.seenDiscoveries.includes(baseId)) {
            this.collection.seenDiscoveries.push(baseId);
            this.gameState.save();
        }
    }

    /**
     * Get count of unseen collection discoveries
     * @returns {number} Number of unseen discovered items
     */
    getUnseenCollectionCount() {
        const found = this.collection?.found || [];
        const seen = this.collection?.seenDiscoveries || [];
        return found.filter(baseId => !seen.includes(baseId)).length;
    }

    /**
     * Mark all current collection discoveries as seen
     */
    markAllCollectionSeen() {
        if (!this.collection) return;
        const found = this.collection.found || [];
        this.collection.seenDiscoveries = [...new Set([
            ...(this.collection.seenDiscoveries || []),
            ...found
        ])];
        this.gameState.save();
    }

    /**
     * Clean up seen discoveries that are no longer in found list
     */
    cleanupSeenCollectionItems() {
        if (!this.collection) return;
        const found = this.collection.found || [];
        this.collection.seenDiscoveries = (this.collection.seenDiscoveries || [])
            .filter(baseId => found.includes(baseId));
    }

    // ===== Tutorial State =====

    /**
     * Check if wave tutorial has been shown
     * @returns {boolean} True if tutorial was shown
     */
    hasTutorialShown() {
        return this.waves.tutorialShown || false;
    }

    /**
     * Mark wave tutorial as shown
     */
    markTutorialShown() {
        this.waves.tutorialShown = true;
        this.gameState.save();
    }
}
